import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import { Card } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { BarChart3, Download, Loader2, CheckCircle2, XCircle, Clock, TrendingUp, Presentation, Lock, ChevronRight, Eye, EyeOff } from 'lucide-react';
import { isMgr } from './demoConstants';
import { computeStats, rollup } from './demoStats';
import DemoPeriodSelector from './DemoPeriodSelector';
import SearchSelect from './SearchSelect';
import DemoDetailModal from './DemoDetailModal';
import { PageHeader, Stat, DemoTable, EmptyState } from './DemoUI';

const csv = (rows) => {
  const head = ['Demo', 'Date', 'Time', 'Type', 'Trainer', 'Counsellor', 'Course', 'Student', 'Status', 'Outcome', 'Enrolment No', 'Enrolled Student', 'Alignment', 'Trainer Comments', 'Counsellor Comments'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const body = rows.map((d) => [d.demo_code, d.demo_date, d.demo_time, d.demo_type, d.trainer_name, d.counsellor_name,
    d.course_name, d.student_name, d.status, d.outcome?.result || '', d.outcome?.enrollment_no || '', d.outcome?.student_name || '', d.feedback_alignment || '',
    d.trainer_feedback?.comments || '', d.counsellor_feedback?.comments || ''].map(esc).join(','));
  return [head.map(esc).join(','), ...body].join('\n');
};

const ROLLUPS = [
  { key: 'trainer', label: 'By Trainer', field: 'trainer_name', head: 'Trainer' },
  { key: 'counsellor', label: 'By Counsellor', field: 'counsellor_name', head: 'Counsellor' },
  { key: 'course', label: 'By Course', field: 'course_name', head: 'Course' },
];

const RollupTable = ({ rows, head, onRowClick, testid }) => (
  <Card className="border overflow-hidden" data-testid={testid}>
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500 font-medium border-b border-slate-200">
          <th className="px-4 py-3">{head}</th><th className="px-4 py-3">Demos</th><th className="px-4 py-3">Admissions</th>
          <th className="px-4 py-3">No Admission</th><th className="px-4 py-3">Follow-ups</th><th className="px-4 py-3">Pending</th>
          <th className="px-4 py-3">Conversion</th><th className="px-4 py-3">Critical Mismatch</th><th className="px-4 py-3 w-8"></th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name} onClick={() => onRowClick(r)} className="border-b border-slate-100 last:border-0 hover:bg-violet-50 transition-colors cursor-pointer" data-testid={`rollup-row-${r.name}`}>
              <td className="px-4 py-3 font-semibold text-slate-800">{r.name}</td>
              <td className="px-4 py-3 tabular-nums text-slate-600">{r.demos}</td>
              <td className="px-4 py-3 tabular-nums text-emerald-700 font-semibold">{r.admissions}</td>
              <td className="px-4 py-3 tabular-nums text-rose-600">{r.no_admissions}</td>
              <td className="px-4 py-3 tabular-nums text-sky-600">{r.followups}</td>
              <td className="px-4 py-3 tabular-nums text-slate-400">{r.pending}</td>
              <td className="px-4 py-3 tabular-nums font-semibold text-violet-700">{r.conversion}%</td>
              <td className="px-4 py-3 tabular-nums text-rose-600">{r.mismatch}</td>
              <td className="px-3 py-3 text-slate-300"><ChevronRight className="w-4 h-4" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Card>
);

export default function DemoReportPage({ embedded = false }) {
  const { user } = useAuth();
  const role = user?.role;
  const mgr = isMgr(role);
  const [period, setPeriod] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [masters, setMasters] = useState({ trainers: [], counsellors: [], courses: [] });
  const [f, setF] = useState({ trainer_id: '', counsellor_id: '', course_id: '', outcome: '' });
  const [tab, setTab] = useState('trainer');
  const [showDemos, setShowDemos] = useState(false);
  const [group, setGroup] = useState(null);      // { label, head, rows }
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    api.get('/training/demos/masters/all')
      .then((r) => setMasters({ trainers: r.data.trainers || [], counsellors: r.data.counsellors || [], courses: r.data.courses || [] }))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!period) return;
    setLoading(true);
    try {
      const params = { date_from: period.from_date, date_to: period.to_date, include_all: 'true' };
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const d = await api.get('/training/demos', { params });
      setRows(d.data.demos || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load report'); }
    finally { setLoading(false); }
  }, [period, f]);
  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => computeStats(rows), [rows]);
  const activeRollup = ROLLUPS.find((r) => r.key === tab);
  const rollupRows = useMemo(() => rollup(rows, activeRollup.field), [rows, activeRollup]);
  const toggle = (k, v) => setF((p) => ({ ...p, [k]: p[k] === v ? '' : v }));

  const download = () => {
    const blob = new Blob([csv(rows)], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `trainer-demos-${period?.from_date}_${period?.to_date}.csv`;
    a.click();
  };

  const openGroup = (r) => setGroup({ label: r.name, head: activeRollup.head, rows: rows.filter((d) => (d[activeRollup.field] || '—') === r.name) });

  return (
    <div className={embedded ? "space-y-6" : "space-y-8"} data-testid="demo-report-page">
      <PageHeader embedded={embedded} icon={BarChart3} eyebrow="Trainer Demo" title="Demo Report" subtitle="Read-only analysis of every demo in the period — no edits can be made here." testid="demo-report-header">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500 border border-slate-200" data-testid="read-only-chip">
          <Lock className="w-3 h-3" />Read only
        </span>
        <Button variant="outline" size="sm" className="h-10" onClick={download} disabled={!rows.length} data-testid="export-csv-btn"><Download className="w-4 h-4 mr-1.5" />Export CSV</Button>
      </PageHeader>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="report-kpi-rail">
        <Stat icon={Presentation} label="Approved Demos" value={stats.total_approved} tone="violet" testid="rkpi-approved" />
        <Stat icon={CheckCircle2} label="Admissions" value={stats.admissions} tone="emerald" onClick={() => toggle('outcome', 'admission')} active={f.outcome === 'admission'} testid="rkpi-admissions" />
        <Stat icon={XCircle} label="No Admission" value={stats.no_admissions} tone="rose" onClick={() => toggle('outcome', 'no_admission')} active={f.outcome === 'no_admission'} testid="rkpi-no-admissions" />
        <Stat icon={Clock} label="Follow-ups" value={stats.followups} tone="sky" onClick={() => toggle('outcome', 'follow_up')} active={f.outcome === 'follow_up'} testid="rkpi-followups" />
        <Stat icon={TrendingUp} label="Conversion" value={`${stats.conversion_pct}%`} tone="amber" sub={`${stats.admissions} of ${stats.total_approved} approved`} testid="rkpi-conversion" />
        <Stat icon={Lock} label="Finalised" value={stats.finalised} tone="slate" testid="rkpi-finalised" />
        <Stat icon={Clock} label="Pending Feedback" value={stats.pending_feedback} tone="slate" sub="awaiting trainer / counsellor" testid="rkpi-pending-feedback" />
      </div>

      <Card className="p-4 border flex flex-wrap items-center gap-2" data-testid="report-filter-bar">
        <DemoPeriodSelector onChange={setPeriod} defaultKey="this_month" />
        {mgr && <SearchSelect value={f.trainer_id} onChange={(v) => setF((p) => ({ ...p, trainer_id: v }))} options={masters.trainers} allowAll allLabel="All trainers" testid="report-filter-trainer" className="w-44" />}
        {(mgr || role === 'trainer') && <SearchSelect value={f.counsellor_id} onChange={(v) => setF((p) => ({ ...p, counsellor_id: v }))} options={masters.counsellors} allowAll allLabel="All counsellors" testid="report-filter-counsellor" className="w-44" />}
        <SearchSelect value={f.course_id} onChange={(v) => setF((p) => ({ ...p, course_id: v }))} options={masters.courses} allowAll allLabel="All courses" testid="report-filter-course" className="w-48" />
        <Select value={f.outcome || 'all'} onValueChange={(v) => setF((p) => ({ ...p, outcome: v === 'all' ? '' : v }))}>
          <SelectTrigger className="h-10 w-44 bg-white text-[13px]" data-testid="report-filter-outcome"><SelectValue placeholder="All outcomes" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All final outcomes</SelectItem>
            <SelectItem value="admission">Admission</SelectItem>
            <SelectItem value="no_admission">No Admission</SelectItem>
            <SelectItem value="follow_up">Follow-up</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="ghost" size="sm" className="h-9 text-[12px] text-slate-500" onClick={() => setF({ trainer_id: '', counsellor_id: '', course_id: '', outcome: '' })} data-testid="report-clear-filters">Clear</Button>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading report…</div>
      ) : rows.length === 0 ? (
        <Card className="border"><EmptyState title="No demos in this period" hint="Try a wider period or clear the filters." /></Card>
      ) : (
        <>
          <section className="space-y-3" data-testid="rollup-section">
            <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wider">Performance rollup</h2>
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="bg-slate-100" data-testid="rollup-tabs">
                {ROLLUPS.map((r) => (
                  <TabsTrigger key={r.key} value={r.key} className="data-[state=active]:bg-white data-[state=active]:shadow text-xs" data-testid={`rollup-tab-${r.key}`}>{r.label}</TabsTrigger>
                ))}
              </TabsList>
              {ROLLUPS.map((r) => (
                <TabsContent key={r.key} value={r.key} className="mt-3">
                  <RollupTable rows={rollupRows} head={r.head} onRowClick={openGroup} testid={`rollup-table-${r.key}`} />
                </TabsContent>
              ))}
            </Tabs>
            <p className="text-[11.5px] text-slate-400">Click any row to see the demos behind the numbers.</p>
          </section>

          <section className="space-y-3" data-testid="demo-list-section">
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="outline" size="sm" className="h-9 text-[12px]" onClick={() => setShowDemos((s) => !s)} data-testid="toggle-demo-table">
                {showDemos ? <><EyeOff className="w-3.5 h-3.5 mr-1.5" />Hide demos</> : <><Eye className="w-3.5 h-3.5 mr-1.5" />Show demos</>}
              </Button>
              <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wider">Demo records <span className="text-slate-400 normal-case">({rows.length})</span></h2>
            </div>
            {showDemos && <DemoTable rows={rows} onRowClick={(d) => setOpenId(d.id)} showTrainer={role !== 'trainer'} showCounsellor={role !== 'counsellor'} />}
          </section>
        </>
      )}

      {/* Rollup drill-down */}
      <Dialog open={!!group} onOpenChange={(o) => { if (!o) setGroup(null); }}>
        <DialogContent className="max-w-6xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="rollup-drilldown-modal">
          <DialogHeader>
            <DialogTitle className="text-base">{group?.head}: {group?.label}</DialogTitle>
            <DialogDescription className="text-[11px] text-slate-500">
              {group?.rows?.length} demos · {period?.label}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {group && (
              <DemoTable rows={group.rows} onRowClick={(d) => { setGroup(null); setOpenId(d.id); }} showTrainer showCounsellor />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <DemoDetailModal demoId={openId} readOnly={!mgr} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}
